import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Question, DisplayLogic } from '../types';
import { displayLogicService } from '../services/displayLogicService';
import DisplayLogicConfig from './DisplayLogicConfig';
import { CheckCircleIcon, AlertCircleIcon, PlusIcon } from './Icons';
import { useToast } from '../context/ToastContext';
import { HelpButton } from './HelpDialog';
import { HELP_DISPLAY_LOGIC } from './HelpContent';

interface DisplayLogicManagerProps {
  questionnaireId: number;
  questions: Question[];
  onUpdate: () => void;
  questionnaireStatus?: string;
}

const DisplayLogicManager: React.FC<DisplayLogicManagerProps> = ({
  questionnaireId,
  questions,
  onUpdate,
  questionnaireStatus,
}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [expandedQuestionId, setExpandedQuestionId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingLogic, setEditingLogic] = useState<DisplayLogic | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  // 筛选模式：all / configured / not-configured
  const [filterMode, setFilterMode] = useState<'all' | 'configured' | 'not-configured'>('all');

  // 检查问卷是否已发布
  const isPublished = questionnaireStatus === 'PUBLISHED';

  const handleSaveLogic = async (questionId: number, logic: DisplayLogic | null) => {
    if (isPublished) {
      showToast('已发布的问卷不允许修改跳转逻辑', 'error');
      return;
    }

    try {
      setSaving(true);
      if (logic && logic.conditions.length > 0) {
        await displayLogicService.setDisplayLogic(questionnaireId, questionId, logic);
      } else {
        await displayLogicService.deleteDisplayLogic(questionnaireId, questionId);
      }
      onUpdate();
      setHasChanges(false);
      setExpandedQuestionId(null);
      showToast('跳转逻辑保存成功', 'success');
    } catch (error) {
      console.error('Failed to save display logic:', error);
      showToast('保存失败，请重试', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleExpand = (questionId: number) => {
    if (hasChanges) {
      if (!window.confirm('有未保存的更改，确定要关闭吗？')) {
        return;
      }
    }
    const question = questions.find(q => q.id === questionId);
    setExpandedQuestionId(questionId);
    setEditingLogic(question?.displayLogic || null);
    setHasChanges(false);
  };

  const handleCollapse = () => {
    if (hasChanges) {
      if (!window.confirm('有未保存的更改，确定要关闭吗？')) {
        return;
      }
    }
    setExpandedQuestionId(null);
    setEditingLogic(null);
    setHasChanges(false);
  };

  const getLogicSummary = (question: Question) => {
    if (!question.displayLogic) return null;

    const { operator, conditions } = question.displayLogic;
    const conditionTexts = conditions.map(c => {
      const sourceIndex = sortedQuestions.findIndex(q => q.id === c.sourceQuestionId);
      if (sourceIndex === -1) return '未知条件';
      return `第${sourceIndex + 1}题`;
    });

    return `当 ${conditionTexts.join(operator === 'AND' ? ' 且 ' : ' 或 ')} 满足条件时显示`;
  };

  /** 题目是否已配置跳转（且条件非空）*/
  const hasLogic = (q: Question) =>
    !!(q.displayLogic && q.displayLogic.conditions && q.displayLogic.conditions.length > 0);

  // 检查是否有足够的问题（需要至少2个问题才能配置跳转）
  if (questions.length < 2) {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        <AlertCircleIcon className="mx-auto mb-4 text-gray-400" size={48} />
        <h3 className="text-lg font-medium text-gray-900 mb-2">需要至少2个问题</h3>
        <p className="text-gray-600 mb-4">
          请先添加至少2个问题，才能配置跳转逻辑
        </p>
        {!isPublished && (
          <button
            onClick={() => navigate(`/questionnaires/${questionnaireId}/questions/new/edit`)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PlusIcon size={20} />
            添加问题
          </button>
        )}
      </div>
    );
  }

  // 按 orderNum 排序问题
  const sortedQuestions = [...questions].sort((a, b) => a.orderNum - b.orderNum);

  // 只显示第2题及之后的题目（第1题不能有条件）
  // 第1题是 sortedQuestions[0]，从第2题开始可以配置
  const configurableQuestions = sortedQuestions.slice(1);
  const configuredCount = configurableQuestions.filter(hasLogic).length;
  const notConfiguredCount = configurableQuestions.length - configuredCount;

  // 按 filterMode 过滤
  const visibleQuestions = configurableQuestions.filter((q) => {
    if (filterMode === 'configured') return hasLogic(q);
    if (filterMode === 'not-configured') return !hasLogic(q);
    return true;
  });

  return (
    <div className="space-y-4">
      {isPublished && (
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-medium text-warning-900 mb-2">⚠️ 已发布问卷</h3>
          <p className="text-sm text-warning-800">
            此问卷已发布，不允许修改跳转逻辑。如需修改，请先归档问卷或创建新版本。
          </p>
        </div>
      )}

      {/* 概览 + 筛选条 — 让用户一眼看到哪些题已配置 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          {/* 已配置 chip：点击筛选只显示已配置 */}
          <button
            onClick={() => setFilterMode(filterMode === 'configured' ? 'all' : 'configured')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filterMode === 'configured'
                ? 'bg-green-600 text-white'
                : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
            }`}
          >
            <CheckCircleIcon size={16} />
            已配置跳转：{configuredCount}
          </button>
          {/* 未配置 chip */}
          <button
            onClick={() => setFilterMode(filterMode === 'not-configured' ? 'all' : 'not-configured')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filterMode === 'not-configured'
                ? 'bg-gray-700 text-white'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-gray-400" />
            未配置：{notConfiguredCount}
          </button>
          {/* 显示全部 */}
          {filterMode !== 'all' && (
            <button
              onClick={() => setFilterMode('all')}
              className="text-xs text-gray-500 hover:text-gray-800 underline"
            >
              显示全部 {configurableQuestions.length} 题
            </button>
          )}
          <div className="flex-1" />
          <HelpButton title="跳转逻辑详解与示例" content={HELP_DISPLAY_LOGIC} />
        </div>
        <p className="text-xs text-gray-500">
          {filterMode === 'all'
            ? `共 ${questions.length} 个问题，其中第 2 题起的 ${configurableQuestions.length} 个可配置跳转逻辑`
            : filterMode === 'configured'
              ? `仅显示已配置跳转的题目（${configuredCount} 题）`
              : `仅显示未配置跳转的题目（${notConfiguredCount} 题）`}
        </p>
      </div>

      {visibleQuestions.length === 0 && (
        <div className="text-center py-10 text-sm text-gray-500 bg-white rounded-lg border border-dashed border-gray-300">
          {filterMode === 'configured'
            ? '还没有配置任何跳转。点击下方任意题目「配置」即可添加。'
            : '所有题目都已配置跳转。'}
        </div>
      )}

      {visibleQuestions.map((question) => {
        const isExpanded = expandedQuestionId === question.id;
        const summary = getLogicSummary(question);
        const configured = hasLogic(question);
        // 当前问题在排序后列表中的索引
        const questionIndex = sortedQuestions.findIndex(q => q.id === question.id);

        return (
          <div
            key={question.id}
            className={`rounded-lg border-2 overflow-hidden transition-colors ${
              configured
                ? 'bg-green-50/40 border-green-300'
                : 'bg-white border-gray-200'
            }`}
          >
            {/* 问题标题行 */}
            <div
              className={`p-4 flex items-center justify-between cursor-pointer ${
                configured ? 'hover:bg-green-50' : 'hover:bg-gray-50'
              }`}
              onClick={() => isExpanded ? handleCollapse() : handleExpand(question.id)}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {/* 序号徽标：已配置用绿色 + 钩；未配置用灰色 */}
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm relative ${
                    configured
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                  title={configured ? '已配置跳转' : '未配置'}
                >
                  {questionIndex + 1}
                  {configured && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center border border-green-600">
                      <CheckCircleIcon size={12} className="text-green-600" />
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-gray-900 truncate">{question.title}</h3>
                    {/* 状态徽章 */}
                    {configured ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                        <CheckCircleIcon size={12} />
                        已配置跳转
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                        无跳转（默认显示）
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">{question.typeDisplayName}</span>
                    {summary && (
                      <span className="text-xs text-green-700">· {summary}</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                className={`flex-shrink-0 px-4 py-1.5 text-sm rounded-md font-medium ${
                  isPublished
                    ? 'text-gray-400 cursor-not-allowed'
                    : isExpanded
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      : configured
                        ? 'bg-white text-green-700 border border-green-300 hover:bg-green-50'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isPublished) {
                    showToast('已发布的问卷不允许修改跳转逻辑', 'error');
                    return;
                  }
                  isExpanded ? handleCollapse() : handleExpand(question.id);
                }}
                disabled={isPublished}
              >
                {isExpanded ? '收起' : configured ? '编辑跳转' : '配置跳转'}
              </button>
            </div>

            {/* 展开的配置区域 */}
            {isExpanded && (
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <DisplayLogicConfig
                  currentQuestion={question}
                  allQuestions={sortedQuestions}
                  value={editingLogic}
                  onChange={(logic) => {
                    setEditingLogic(logic);
                    setHasChanges(true);
                  }}
                />
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={handleCollapse}
                    className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-white"
                    disabled={saving}
                  >
                    取消
                  </button>
                  <button
                    onClick={() => handleSaveLogic(question.id, editingLogic)}
                    className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    disabled={saving || !hasChanges}
                  >
                    {saving ? '保存中...' : '保存'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DisplayLogicManager;
